import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  CreditCard,
  RefreshCw,
  XCircle,
  HelpCircle,
  AlertTriangle,
  Mail,
  Globe,
  Copy,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  FileText,
  Bookmark,
  Calendar,
  Check
} from "lucide-react";
import Footer from "../components/Footer";
import "./fundspolicy.css";

const FundsPolicy = () => {
  const [activeSection, setActiveSection] = useState("sec1");
  const [copied, setCopied] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);


  // Sections data for Table of Contents
  const sections = [
    { id: "sec1", number: "1", title: "Premium Subscription" },
    { id: "sec2", number: "2", title: "Payment Method" },
    { id: "sec3", number: "3", title: "Subscription Renewal" },
    { id: "sec4", number: "4", title: "Cancellation Policy" },
    { id: "sec5", number: "5", title: "Refund Policy" },
    { id: "sec6", number: "6", title: "Failed or Pending Payments" },
    { id: "sec7", number: "7", title: "Changes to Plans" },
    { id: "sec8", number: "8", title: "Contact Us" }
  ];

  // Dynamic scroll spy and scroll progress indicator
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }


    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 100;
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth"
      });
      setActiveSection(id);
    }
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("support@fastinvoice.in");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="funds-policy-page">


      {/* GLOW DECORATIONS */}
      <div className="bg-glow glow-top-right"></div>
      <div className="bg-glow glow-bottom-left"></div>

      {/* HERO BANNER */}
      <section className="policy-hero">
        <div className="policy-hero-content">
          <div className="gst-badge">
            <span className="badge-dot"></span> Made for Indian Businesses • GST Ready
          </div>
          <h1>Refund & Cancellation Policy</h1>
          <p className="hero-subtext">
            Clear, simple terms regarding subscription billing, auto-renewals, cancellations, and refunds.
          </p>
          <div className="meta-info">
            <div className="meta-item">
              <Calendar size={16} />
              <span>Effective Date: <strong>June 29, 2026</strong></span>
            </div>
            <div className="meta-item">
              <ShieldCheck size={16} />
              <span>Payment Gateway: <strong>Razorpay Secured</strong></span>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT SECTION WITH SIDEBAR */}
      <main className="policy-main-container">
        <div className="policy-layout">
          
          {/* LEFT SIDEBAR - TABLE OF CONTENTS */}
          <aside className="policy-sidebar">
            <div className="sidebar-sticky-wrapper">
              <h3>Table of Contents</h3>
              <ul className="toc-list">
                {sections.map((section) => (
                  <li key={section.id}>
                    <button
                      className={`toc-link ${activeSection === section.id ? "active" : ""}`}
                      onClick={() => scrollToSection(section.id)}
                    >
                      <span className="toc-num">{section.number}</span>
                      <span className="toc-title">{section.title}</span>
                    </button>
                  </li>
                ))}
              </ul>

              <div className="sidebar-cta">
                <h4>Need quick help?</h4>
                <p>Reach out directly to our dedicated support channels.</p>
                <a href="mailto:support@fastinvoice.in" className="cta-email-btn">
                  <Mail size={16} />
                  Email Support
                </a>
              </div>
            </div>
          </aside>

          {/* RIGHT COLUMN - POLICY DETAILS */}
          <article className="policy-content">
            
            {/* INTRO */}
            <div className="policy-intro-card">
              <p>
                Thank you for choosing <strong>FastInvoice</strong>. This Refund & Cancellation Policy explains how subscription cancellations, refunds, and billing are handled for purchases made through the FastInvoice website.
              </p>
            </div>

            {/* SECTION 1: Premium Subscription */}
            <section id="sec1" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <Bookmark className="sec-icon" />
                </div>
                <h2>1. Premium Subscription</h2>
              </div>
              <p>
                FastInvoice offers Premium subscription plans that provide access to additional features, including but not limited to:
              </p>
              
              <div className="features-grid">
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Invoice Credits</strong>
                    <p>Allocation based on your selected subscription plan</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Inventory Management</strong>
                    <p>Track stocks, low levels alerts, and item details</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Advanced Business Features</strong>
                    <p>Detailed reports, analytics, multi-company support</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Priority Customer Support</strong>
                    <p>Faster responses and dedicated assistance channels</p>
                  </div>
                </div>
                <div className="feature-item-card">
                  <div className="item-check"><Check size={14} /></div>
                  <div className="item-text">
                    <strong>Future Premium Updates</strong>
                    <p>Automatic access to newly added Premium capabilities</p>
                  </div>
                </div>
              </div>

              <p className="subtext-para">
                Premium subscriptions are available on monthly and yearly billing plans.
              </p>
            </section>

            {/* SECTION 2: Payment Method */}
            <section id="sec2" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <CreditCard className="sec-icon" />
                </div>
                <h2>2. Payment Method</h2>
              </div>
              <p>
                Payments on the FastInvoice website are securely processed through <strong>Razorpay</strong>.
              </p>
              <div className="highlight-callout secure-callout">
                <div className="callout-icon">
                  <ShieldCheck size={24} />
                </div>
                <div className="callout-content">
                  <h4>Industry-Standard Security</h4>
                  <p>
                    We do not store your credit card, debit card, UPI, net banking, or other payment information on our servers. All payment information is securely handled by Razorpay in accordance with industry security standards.
                  </p>
                </div>
              </div>
            </section>

            {/* SECTION 3: Subscription Renewal */}
            <section id="sec3" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <RefreshCw className="sec-icon" />
                </div>
                <h2>3. Subscription Renewal</h2>
              </div>
              <p>
                Premium subscriptions may automatically renew at the end of each billing cycle unless cancelled before the renewal date.
              </p>
              <ul className="custom-bullets">
                <li>
                  <span className="bullet-indicator"></span>
                  By purchasing a subscription, you authorize FastInvoice and its payment provider to process recurring payments where applicable.
                </li>
                <li>
                  <span className="bullet-indicator"></span>
                  You may disable automatic renewal at any time before your next billing cycle.
                </li>
              </ul>
            </section>

            {/* SECTION 4: Cancellation Policy */}
            <section id="sec4" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <XCircle className="sec-icon" />
                </div>
                <h2>4. Cancellation Policy</h2>
              </div>
              <p>
                You may cancel your Premium subscription at any time.
              </p>
              
              <div className="cancellation-flow">
                <div className="flow-step">
                  <div className="flow-badge">After Cancellation</div>
                  <div className="flow-desc">
                    Your Premium access will remain active until the end of your current billing period. No future subscription charges will be made.
                  </div>
                </div>
                <div className="flow-step warning-step">
                  <div className="flow-badge">Upon Expiration</div>
                  <div className="flow-desc">
                    Once the subscription expires, Premium features, including Inventory Management and any remaining plan-based invoice credits, will no longer be available.
                  </div>
                </div>
              </div>

              <div className="alert-box note-alert">
                <strong>Please Note:</strong> Cancelling a subscription does not automatically qualify you for a refund.
              </div>
            </section>

            {/* SECTION 5: Refund Policy */}
            <section id="sec5" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <HelpCircle className="sec-icon" />
                </div>
                <h2>5. Refund Policy</h2>
              </div>
              <p>
                Due to the nature of our digital subscription services, all subscription payments are generally non-refundable once Premium features have been activated.
              </p>

              <div className="refund-conditions-title">
                Refund requests may only be considered under the following circumstances:
              </div>

              <div className="refund-cases-grid">
                <div className="case-card">
                  <span className="case-num">01</span>
                  <h4>Duplicate Payment</h4>
                  <p>Caused by a technical issue or connection dropout during processing.</p>
                </div>
                <div className="case-card">
                  <span className="case-num">02</span>
                  <h4>Incorrect Billing</h4>
                  <p>Errors caused directly by our system billing mechanisms.</p>
                </div>
                <div className="case-card">
                  <span className="case-num">03</span>
                  <h4>Non-Activation</h4>
                  <p>Payment was successfully charged but purchased subscription or credits were not activated.</p>
                </div>
                <div className="case-card">
                  <span className="case-num">04</span>
                  <h4>Applicable Law</h4>
                  <p>Refund is explicitly required under governing local laws or regulations.</p>
                </div>
              </div>

              <div className="highlight-callout teal-callout">
                <p>
                  If a refund is approved, the amount will be returned to the original payment method used during the purchase.
                </p>
              </div>
            </section>

            {/* SECTION 6: Failed or Pending Payments */}
            <section id="sec6" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <AlertTriangle className="sec-icon" />
                </div>
                <h2>6. Failed or Pending Payments</h2>
              </div>
              <p>
                If your payment fails but the amount has been deducted from your account, the transaction is generally reversed automatically by your bank or payment provider within their standard processing period.
              </p>
              <div className="alert-box info-alert">
                If the amount is not reversed within the expected timeframe, please contact us with your payment details (transaction ID, email, date) for manual check.
              </div>
            </section>

            {/* SECTION 7: Changes to Subscription Plans */}
            <section id="sec7" className="content-section">
              <div className="section-header">
                <div className="header-icon-box">
                  <FileText className="sec-icon" />
                </div>
                <h2>7. Changes to Subscription Plans</h2>
              </div>
              <p>
                FastInvoice reserves the right to modify subscription plans, pricing, Premium features, invoice credit allocations, or billing terms at any time.
              </p>
              <p className="subtext-para">
                Any changes will not affect your current subscription period and will apply from your next renewal unless otherwise stated.
              </p>
            </section>

            {/* SECTION 8: Contact Us */}
            <section id="sec8" className="content-section contact-section-card">
              <div className="section-header">
                <div className="header-icon-box">
                  <Mail className="sec-icon" />
                </div>
                <h2>8. Contact Us</h2>
              </div>
              <p>
                If you have any questions regarding subscriptions, cancellations, refunds, or billing, please reach out to us:
              </p>

              <div className="contact-details-box">
                <div className="company-meta">
                  <h3>FastInvoice</h3>
                  <p>Invoicing & Inventory Management Solutions</p>
                </div>

                <div className="contact-links-grid">
                  <div className="contact-card-item">
                    <Globe className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Official Website</span>
                      <a href="https://fastinvoice.in" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                        https://fastinvoice.in
                      </a>
                    </div>
                  </div>

                  <div className="contact-card-item">
                    <Mail className="contact-card-icon" />
                    <div>
                      <span className="contact-card-label">Support Email</span>
                      <div className="email-copy-wrapper">
                        <a href="mailto:support@fastinvoice.in" className="contact-card-link">
                          support@fastinvoice.in
                        </a>
                        <button 
                          className="copy-btn" 
                          onClick={handleCopyEmail}
                          title="Copy Email Address"
                        >
                          {copied ? <CheckCircle size={16} className="copied-icon" /> : <Copy size={16} />}
                          <span className="copy-tooltip">{copied ? "Copied!" : "Copy"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

          </article>
        </div>
      </main>

      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default FundsPolicy;
